/**
 * Soft-tissue leaf bones — ADR-038 J2: **one grafted leaf per region, pivoted
 * at the tissue's root, and the contained weights that make it move the mass.**
 *
 * ONE responsibility, two functions (the tail's graft/bind split):
 *
 *  1. {@link graftSoftTissueLeaves} — per region, the leaf `<Slot>_graft` and
 *     its weightless tip `<Slot>_End_graft` (BBSA 3.5.0 soft-tissue slots,
 *     provenance `grafted`, excluded from every profile). The leaf's head is
 *     the region's ROOT (the centroid of its attachment ring — on or inside the
 *     body wall behind the mass), its tip the APEX, so a rotation of the leaf
 *     swings the mass about where it grows from. Parents: a breast hangs from
 *     the chest (Spine2), the belly from the waist (Spine), a glute from the
 *     pelvis — the bone the thighs hang from (ADR-029's owner), else Hips; the
 *     HAIR (ADR-039 T2a, `HairJiggle`) from the Head, its root at the confirmed
 *     skull's centre and its tip in the hair (the app's hair pass places both).
 *     Every leaf rests in its PARENT's frame (local rest = identity, the
 *     tail's contract), so a spring's swing is the same small rotation in the
 *     body's axes whatever the flesh's shape.
 *  2. {@link softTissueInfluences} — the region's feather as the leaf's
 *     share: peak × f(v), 1 at the apex falling smoothly to 0 on the ring.
 *     The ONE seat (`bindGraftedDigits`) scales every existing influence of a
 *     vertex by (1 − share) and adds the leaf's share — so the weights under
 *     the leaf (ADR-021's chest repair, ADR-029's pelvis re-own, the hinge
 *     bands) keep their proportions, and nothing OUTSIDE the region moves.
 *
 * REST INVARIANCE is by construction: a grafted bone's inverse bind is the
 * inverse of the world bind declared here, so at rest boneWorld · IBM = I and
 * every blend of rest transforms is the identity — the mesh does not move a
 * bit when the leaf takes weight (measured in the specimen probe).
 *
 * Depends on: jiggle/regions, ir/rig-descriptor, hands/graft-digits
 * (localBindFromParent), hands/bind-weights (the influence type).
 * Depended on by: the app's run (the replay), the specimen probe.
 */
import { KernelError } from '../ir/errors.js';
import { parseRigDescriptor, type MappingEntry, type RigBone, type RigDescriptor } from '../ir/rig-descriptor.js';
import type { Vec3Data } from '../math/vec3.js';
import type { VertexInfluence } from '../hands/bind-weights.js';
import { localBindFromParent } from '../hands/graft-digits.js';
import { kindOfRegion, type JiggleRegionId, type SoftTissueRegion } from './regions.js';

/** The BBSA slot each region grafts (3.5.0; the hair's 3.6.0, ADR-039 T2a). */
export const SOFT_TISSUE_SLOT: Readonly<Record<JiggleRegionId, string>> = {
  LeftBreast: 'LeftBreastJiggle',
  RightBreast: 'RightBreastJiggle',
  Belly: 'BellyJiggle',
  LeftGlute: 'LeftGluteJiggle',
  RightGlute: 'RightGluteJiggle',
  Hair: 'HairJiggle',
};

/** The share of a vertex the leaf takes at the apex (the feather scales it down to 0 at the ring). */
export const SOFT_TISSUE_PEAK_SHARE = 1;

/** A person must act (a rig that already wears the leaf, a region too small to swing). */
export class SoftTissueRefusal extends KernelError {}

export interface GraftedLeaf {
  readonly region: JiggleRegionId;
  /** The leaf's rig name (`LeftBreastJiggle_graft`). */
  readonly bone: string;
  /** Its weightless tip (`LeftBreastJiggle_End_graft`). */
  readonly tip: string;
  readonly parent: string;
  /** Bind-world head (the root) and tip (the apex). */
  readonly head: Vec3Data;
  readonly tail: Vec3Data;
  /** |tail − head|, world units. */
  readonly length: number;
}

export interface GraftSoftTissueResult {
  readonly rig: RigDescriptor;
  readonly mappingAdditions: readonly MappingEntry[];
  readonly leaves: readonly GraftedLeaf[];
}

/** The rig bone a region's leaf hangs from, or null when the rig maps none of its candidates. */
export function softTissueParentBone(rig: RigDescriptor, region: JiggleRegionId): string | null {
  const mapped = (id: string): string | undefined => rig.mapping.find((m) => m.ontologyId === id)?.rigBone;
  const kind = kindOfRegion(region);
  // The hair's leaf hangs from the Head (ADR-039 T2a): the confirmed region carries Harry's Head weights.
  if (kind === 'hair') return mapped('Head') ?? null;
  if (kind === 'glute') {
    // The pelvis OWNER — the bone the thighs hang from (ADR-029), which on a
    // Tripo rig is `Pelvis`, a child of the mapped Hips; else Hips itself.
    const thigh = mapped(region === 'LeftGlute' ? 'LeftUpLeg' : 'RightUpLeg');
    const parent = thigh === undefined ? undefined : rig.bones.find((b) => b.name === thigh)?.parent;
    return parent ?? mapped('Hips') ?? null;
  }
  const order = kind === 'breast' ? ['Spine2', 'Spine1', 'Spine'] : ['Spine', 'Hips'];
  for (const id of order) {
    const b = mapped(id);
    if (b !== undefined) return b;
  }
  return null;
}

/**
 * Graft one leaf (and its tip) per region onto the rig. Refuses by name a rig
 * that already carries a slot (a reopened export wears its leaves — adopt its
 * spring, never stack a second leaf inside the first), a region the rig has no
 * parent for, and a leaf of no length.
 */
export function graftSoftTissueLeaves(
  rig: RigDescriptor,
  regions: readonly (Omit<Pick<SoftTissueRegion, 'id' | 'root' | 'apex'>, 'id'> & { readonly id: JiggleRegionId })[],
  /**
   * The bone that OWNS each region's flesh, measured from the paint (the
   * feather-weighted majority). A breast or belly leaf hangs from it, so the
   * leaf at rest follows the animation the way the flesh it takes over did.
   * Field read 2026-09-27, Amelie: her small breast is 75% `Spine01`; hung from
   * `Spine02` by name, the leaf's centre vertex (100% leaf) sat 26 mm off the
   * leafless body under a walk with NO physics — a point. Absent or not a rig
   * bone: the named order ({@link softTissueParentBone}). A glute keeps
   * ADR-029's pelvis owner.
   */
  owners: ReadonlyMap<JiggleRegionId, string> = new Map(),
): GraftSoftTissueResult {
  const existing = new Set(rig.bones.map((b) => b.name));
  const mappedIds = new Set(rig.mapping.map((m) => m.ontologyId));
  const bones: RigBone[] = [];
  const mappingAdditions: MappingEntry[] = [];
  const leaves: GraftedLeaf[] = [];
  const seen = new Set<JiggleRegionId>();
  for (const r of regions) {
    if (seen.has(r.id)) throw new SoftTissueRefusal(`${r.id} is declared twice — one leaf per region`);
    seen.add(r.id);
    const slot = SOFT_TISSUE_SLOT[r.id];
    const clash = [slot, `${slot}_graft`, `${slot}_End`, `${slot}_End_graft`].filter((n) => existing.has(n));
    if (clash.length > 0 || mappedIds.has(slot)) {
      throw new SoftTissueRefusal(
        `this rig already carries a ${r.id} leaf (${clash[0] ?? slot}) — a reopened export wears its leaves; its spring is adopted from the file.`,
      );
    }
    const bust = nativeBustBones(rig);
    const native = r.id === 'LeftBreast' ? bust.left : r.id === 'RightBreast' ? bust.right : [];
    if (native.length > 0) {
      throw new SoftTissueRefusal(`${r.id}: this body carries its own bust bones (${native.join(', ')}) — they are its breast spring; a leaf would be a second mass in the same flesh`);
    }
    const owner = kindOfRegion(r.id) === 'glute' ? undefined : owners.get(r.id);
    const parentName = owner !== undefined && rig.bones.some((b) => b.name === owner) ? owner : softTissueParentBone(rig, r.id);
    const parent = parentName === null ? undefined : rig.bones.find((b) => b.name === parentName);
    if (parent === undefined) throw new SoftTissueRefusal(`${r.id}: the rig maps no bone for the leaf to hang from`);
    const length = Math.hypot(r.apex.x - r.root.x, r.apex.y - r.root.y, r.apex.z - r.root.z);
    if (!(length > 1e-5)) {
      throw new SoftTissueRefusal(`${r.id}: the apex sits on its own root (no mass stands off the body) — nothing for a leaf to swing`);
    }
    const headWorld = { position: r.root, rotation: parent.worldBind.rotation, scale: { x: 1, y: 1, z: 1 } };
    const tipWorld = { position: r.apex, rotation: parent.worldBind.rotation, scale: { x: 1, y: 1, z: 1 } };
    const bone = `${slot}_graft`;
    const tip = `${slot}_End_graft`;
    bones.push(
      { name: bone, parent: parent.name, localBind: localBindFromParent(parent.worldBind, headWorld), worldBind: headWorld },
      { name: tip, parent: bone, localBind: localBindFromParent(headWorld, tipWorld), worldBind: tipWorld },
    );
    mappingAdditions.push(
      { rigBone: bone, ontologyId: slot, confidence: 0.9, provenance: 'grafted' },
      { rigBone: tip, ontologyId: `${slot}_End`, confidence: 0.9, provenance: 'grafted' },
    );
    leaves.push({ region: r.id, bone, tip, parent: parent.name, head: r.root, tail: r.apex, length });
  }
  if (bones.length === 0) return { rig, mappingAdditions: [], leaves: [] };
  const next = parseRigDescriptor({ ...rig, bones: [...rig.bones, ...bones], mapping: [...rig.mapping, ...mappingAdditions] });
  return { rig: next, mappingAdditions, leaves };
}

/**
 * The breast bones a body already carries — VRoid's `J_Sec_L_Bust1/2`,
 * `J_Sec_R_Bust1/2` (World's name-door springs, ADR-038 §6). A breast leaf is
 * NEVER grafted over them: that would be a second mass in the same flesh.
 */
export function nativeBustBones(rig: Pick<RigDescriptor, 'bones'>): { left: readonly string[]; right: readonly string[] } {
  const names = rig.bones.map((b) => b.name);
  return { left: names.filter((n) => /^J_Sec_L_Bust\d/.test(n)), right: names.filter((n) => /^J_Sec_R_Bust\d/.test(n)) };
}

/** A thrown condition that is a wiring bug (the bone names do not match the regions). */
export class SoftTissueGraftError extends KernelError {}

/**
 * The leaf's influences over its region: `peak × feather` per vertex, zero
 * shares dropped. The seat renormalises the rest of each vertex against it.
 */
export function softTissueInfluences(
  region: Omit<Pick<SoftTissueRegion, 'id' | 'vertices' | 'feather'>, 'id'> & { readonly id: JiggleRegionId },
  leafBone: string,
  peak: number = SOFT_TISSUE_PEAK_SHARE,
): VertexInfluence[] {
  if (region.vertices.length !== region.feather.length) {
    throw new SoftTissueGraftError(`${region.id}: ${region.vertices.length} vertices but ${region.feather.length} feather values`);
  }
  if (!(peak > 0 && peak <= 1)) throw new SoftTissueGraftError(`${region.id}: peak share ${peak} outside (0, 1]`);
  const out: VertexInfluence[] = [];
  region.vertices.forEach((v, k) => {
    const w = peak * region.feather[k]!;
    if (w > 0) out.push({ vertexIndex: v, boneId: leafBone, weight: w });
  });
  return out;
}
